import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button, Card, CardBody, CardHeader, Chip, Spinner } from "@heroui/react";
import {
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowsUpDownIcon,
  PlusIcon,
  ArrowsRightLeftIcon,
  MinusCircleIcon,
  XMarkIcon,
  CubeIcon,
  QrCodeIcon,
} from "@heroicons/react/24/outline";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { ListCommanWay, CreateCommanWay, GetCommanWay } from "API/commonApi";
import Select from "react-select";
import UseToast from "utils/AfToast";
import { EyeIcon } from "@heroicons/react/24/outline";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";
import { STOCK_ITEM_OPTIONS, CATEGORY_OPTIONS } from "utils/constant";
import { isBoxQrFormat, isAwbFormat } from "pages/BoxTracker/components/AwbPackingTab";
import { soundFX } from "pages/BoxTracker/soundEffects";

const StockReport: React.FC = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<any[]>([]);
  const [sortStat, setSortStat] = useState<any>({});
  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const [filterStartDt, setFilterStartDt] = useState<string>("");
  const [filterEndDt, setFilterEndDt] = useState<string>("");
  const [allTime, setAllTime] = useState(true);
  const [detailViewRow, setDetailViewRow] = useState<number | null>(null);

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [useStockModalOpen, setUseStockModalOpen] = useState(false);

  // PO lookup state
  const [poLookupNo, setPoLookupNo] = useState("");
  const [poLookupData, setPoLookupData] = useState<any>(null);
  const [poLookupLoading, setPoLookupLoading] = useState(false);

  // Add stock form - shared fields
  const [addShared, setAddShared] = useState({
    subbranch: null as any,
    inwardDate: "",
    requiredDeliveryDate: "",
    inspectionDetails: "",
    rejReason: "",
    note: "",
  });

  // Add stock form - per-item rows
  const [addItems, setAddItems] = useState<Array<{
    name: any;
    desc: string;
    category: any;
    qty: string;
    measureUnit: string;
    rejQty: string;
    poItemIndex: number | null;
  }>>([{ name: null, desc: "", category: null, qty: "", measureUnit: "Pcs", rejQty: "", poItemIndex: null }]);

  // Transfer stock form (In-Transit & QR-Tracked)
  const [transferMode, setTransferMode] = useState<"bulk" | "qr">("bulk");
  const [transferQrInput, setTransferQrInput] = useState("");
  const [transferQrCodes, setTransferQrCodes] = useState<string[]>([]);
  const transferQrInputRef = useRef<HTMLInputElement>(null);
  const [transferForm, setTransferForm] = useState({
    name: STOCK_ITEM_OPTIONS[2] || STOCK_ITEM_OPTIONS[0], // "5 Ltr" or first
    category: CATEGORY_OPTIONS[0],
    qty: "5",
    fromBranch: null as any,
    toBranch: null as any,
    note: "",
    inwardDate: "",
  });

  // Use stock form
  const [useForm, setUseForm] = useState({
    name: null as any,
    qty: "",
    subbranch: null as any,
    note: "",
    inwardDate: "",
  });

  const tableRef = useRef<HTMLTableElement>(null);

  // Fetch branches
  useQuery(
    ["SubbranchListApi"],
    () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }),
    {
      onSuccess(data) {
        if (data.status === "SUCCESS") {
          const opt = data.data.data.map((p: any) => ({
            value: p.id,
            label: p.branch_name,
          }));
          setBranchOptions(opt);
        }
      },
    }
  );

  // Build filters object for API
  const buildFilters = () => {
    const f: any = {};
    if (selectedBranch?.value) f.subbranch = selectedBranch.value;
    if (!allTime && (filterStartDt || filterEndDt)) {
      f.inwardDate = {};
      if (filterStartDt) f.inwardDate.$gte = filterStartDt;
      if (filterEndDt) f.inwardDate.$lte = filterEndDt + " 23:59:59";
    }
    return f;
  };

  // Fetch inventory list
  const { isFetching, isLoading, refetch } = useQuery(
    ["inventory_list"],
    () =>
      ListCommanWay(
        "/admin/inventory/list",
        buildFilters(),
        { paginate: 1000, include: ["_subbranch", "_addedBy", "_purchaseOrder"] }
      ),
    {
      keepPreviousData: true,
      enabled: false,
      onSuccess(data) {
        if (data.status === "SUCCESS" && data.data) {
          setTableData(data.data.data || []);
        } else {
          setTableData([]);
        }
      },
      onError() {
        setTableData([]);
      },
    }
  );

  // Initial fetch on mount
  useEffect(() => {
    refetch();
  }, []);

  // Bulk create inventory mutation
  const { mutate: addBulkInventory, isLoading: isCreating } = useMutation(
    (data: any) => CreateCommanWay({ url: "/admin/inventory/addBulk", data }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Stock added successfully!", "success");
          setAddModalOpen(false);
          resetAddForm();
          refetch();
        } else {
          UseToast(data.message || "Failed to add stock", "error");
        }
      },
      onError: () => {
        UseToast("Failed to add stock", "error");
      },
    }
  );

  // Transfer stock mutation (Inter-branch in-transit dispatch)
  const { mutate: transferStock, isLoading: isTransferring } = useMutation(
    (payload: any) =>
      CreateCommanWay({
        url: "/admin/inventory-transfer/dispatch",
        data: payload,
      }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS" && res?.data?.success) {
          soundFX.playSuccess();
          const t = res.data.transfer;
          UseToast(
            res.data.message || `Transfer ${t?.transferNo || ""} dispatched successfully! Stock is in-transit to destination.`,
            "success"
          );
          setTransferModalOpen(false);
          resetTransferForm();
          refetch();
        } else {
          soundFX.playError();
          UseToast(res?.message || res?.data?.message || "Failed to dispatch transfer", "error");
        }
      },
      onError: (err: any) => {
        soundFX.playError();
        UseToast(typeof err === "string" ? err : err?.message || "Failed to dispatch transfer", "error");
      },
    }
  );

  // Use stock mutation (subtracts qty)
  const { mutate: useStock, isLoading: isUsingStock } = useMutation(
    (data: any) =>
      CreateCommanWay({
        url: "/admin/inventory/create",
        data: {
          ...data,
          qty: -Math.abs(data.qty),
        },
      }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Stock usage recorded successfully!", "success");
          setUseStockModalOpen(false);
          resetUseForm();
          refetch();
        } else {
          UseToast(data.message || "Failed to record stock usage", "error");
        }
      },
      onError: () => {
        UseToast("Failed to record stock usage", "error");
      },
    }
  );

  // PO lookup function
  const handlePoLookup = async () => {
    if (!poLookupNo.trim()) {
      UseToast("Please enter a PO number", "error");
      return;
    }
    setPoLookupLoading(true);
    try {
      const res: any = await GetCommanWay(`/admin/inventory/po-lookup?poNo=${poLookupNo.trim()}`, {}, {});
      if (res?.status === "SUCCESS" && res?.data) {
        const poData = {
          ...res.data,
          items: typeof res.data.items === "string" ? JSON.parse(res.data.items) : res.data.items || [],
        };
        setPoLookupData(poData);
        // Auto-fill branch from PO shipToBranch
        if (res.data.shipToBranch) {
          const found = branchOptions.find((b: any) => b.value === res.data.shipToBranch);
          if (found) setAddShared((prev) => ({ ...prev, subbranch: found }));
        }
        // Populate all PO items with qty=0
        if (poData.items?.length) {
          const rows = poData.items.map((it: any, idx: number) => {
            const itemName = it.name || it.description || "";
            const matchingOption = STOCK_ITEM_OPTIONS.find((o) => o.value.toLowerCase() === itemName.toLowerCase());
            return {
              name: matchingOption || { value: itemName, label: itemName },
              desc: it.description || "",
              category: null,
              qty: "",
              measureUnit: it.unit || "Pcs",
              rejQty: "",
              poItemIndex: idx,
            };
          });
          setAddItems(rows);
        }
      } else {
        UseToast(res?.message || "PO not found", "error");
        setPoLookupData(null);
      }
    } catch (err: any) {
      UseToast(err?.message || "PO not found", "error");
      setPoLookupData(null);
    }
    setPoLookupLoading(false);
  };

  // Update a single item row
  const updateAddItem = (index: number, field: string, value: any) => {
    setAddItems((prev) => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  // Add a blank row
  const addNewItemRow = () => {
    setAddItems((prev) => [...prev, { name: null, desc: "", category: null, qty: "", measureUnit: "Pcs", rejQty: "", poItemIndex: null }]);
  };

  // Remove a row
  const removeItemRow = (index: number) => {
    setAddItems((prev) => prev.filter((_, i) => i !== index));
  };

  const submitAddStock = () => {
    const validItems = addItems.filter((it) => it.name?.value && Number(it.qty) > 0);
    const payload = validItems.map((it) => ({
      name: it.name?.value === "OTHER" ? it.desc : it.name?.value,
      description: it.desc || undefined,
      category: it.category?.value || "packaging",
      measureUnit: it.measureUnit || "Pcs",
      qty: Number(it.qty),
      subbranch: addShared.subbranch?.value,
      purchaseOrderId: poLookupData?.id || undefined,
      poItemIndex: it.poItemIndex != null ? it.poItemIndex : undefined,
      inwardDate: addShared.inwardDate || undefined,
      requiredDeliveryDate: addShared.requiredDeliveryDate || undefined,
      inspectionDetails: addShared.inspectionDetails || undefined,
      rejQty: it.rejQty ? Number(it.rejQty) : undefined,
      rejReason: addShared.rejReason || undefined,
      note: addShared.note || undefined,
    }));
    addBulkInventory({ data: payload });
  };

  const resetAddForm = () => {
    setAddShared({
      subbranch: null,
      inwardDate: "",
      requiredDeliveryDate: "",
      inspectionDetails: "",
      rejReason: "",
      note: "",
    });
    setAddItems([{ name: null, desc: "", category: null, qty: "", measureUnit: "Pcs", rejQty: "", poItemIndex: null }]);
    setPoLookupNo("");
    setPoLookupData(null);
  };

  const handleAddStock = () => {
    if (!addShared.subbranch?.value) {
      UseToast("Please select a branch", "error");
      return;
    }
    const validItems = addItems.filter((it) => it.name?.value && Number(it.qty) > 0);
    if (validItems.length === 0) {
      UseToast("Please add at least one item with quantity", "error");
      return;
    }
    for (const it of validItems) {
      if (it.name.value === "OTHER" && !it.desc.trim()) {
        UseToast("Please enter description for custom items", "error");
        return;
      }
    }
    submitAddStock();
  };

  const handleAddTransferQr = (e: React.FormEvent) => {
    e.preventDefault();
    const raw = transferQrInput.trim().toUpperCase();
    if (!raw) return;

    if (isAwbFormat(raw)) {
      soundFX.playError();
      UseToast(`'${raw}' is an AWB number! Stock transfer requires Box QRs (e.g. 123123, BX-123123, AHD123123).`, "error");
      setTransferQrInput("");
      return;
    }

    if (!isBoxQrFormat(raw)) {
      soundFX.playError();
      UseToast(`Invalid Box QR format '${raw}'. Valid: numeric (123123) or prefixed (BX-123123, AHD123123).`, "warning");
      return;
    }

    if (transferQrCodes.includes(raw)) {
      soundFX.playError();
      UseToast(`Box '${raw}' is already added!`, "warning");
      setTransferQrInput("");
      return;
    }

    soundFX.playSuccess();
    setTransferQrCodes((prev) => [...prev, raw]);
    setTransferQrInput("");
    transferQrInputRef.current?.focus();
  };

  const handleRemoveTransferQr = (code: string) => {
    setTransferQrCodes((prev) => prev.filter((c) => c !== code));
  };

  const resetTransferForm = () => {
    setTransferMode("bulk");
    setTransferQrCodes([]);
    setTransferQrInput("");
    setTransferForm({
      name: STOCK_ITEM_OPTIONS[2] || STOCK_ITEM_OPTIONS[0],
      category: CATEGORY_OPTIONS[0],
      qty: "5",
      fromBranch: null,
      toBranch: null,
      note: "",
      inwardDate: "",
    });
  };

  const resetUseForm = () => {
    setUseForm({
      name: null,
      qty: "",
      subbranch: null,
      note: "",
      inwardDate: "",
    });
  };

  const handleTransferStock = () => {
    if (!transferForm.name?.value) {
      UseToast("Please select an item name", "error");
      return;
    }
    if (!transferForm.fromBranch?.value) {
      UseToast("Please select source (From) branch", "error");
      return;
    }
    if (!transferForm.toBranch?.value) {
      UseToast("Please select destination (To) branch", "error");
      return;
    }
    if (transferForm.fromBranch.value === transferForm.toBranch.value) {
      UseToast("Source and destination branch cannot be same", "error");
      return;
    }

    if (transferMode === "qr") {
      if (transferQrCodes.length === 0) {
        UseToast("Please scan or add at least one Box QR code for this transfer!", "error");
        return;
      }
      transferStock({
        fromSubbranch: transferForm.fromBranch.value,
        toSubbranch: transferForm.toBranch.value,
        name: transferForm.name.value,
        category: transferForm.category?.value?.toLowerCase() || "packaging",
        qrCodes: transferQrCodes,
        dispatchNote: transferForm.note.trim() || undefined,
      });
    } else {
      const q = Number(transferForm.qty);
      if (isNaN(q) || q <= 0) {
        UseToast("Please enter a valid transfer quantity", "error");
        return;
      }
      transferStock({
        fromSubbranch: transferForm.fromBranch.value,
        toSubbranch: transferForm.toBranch.value,
        name: transferForm.name.value,
        category: transferForm.category?.value?.toLowerCase() || "packaging",
        qty: q,
        dispatchNote: transferForm.note.trim() || undefined,
      });
    }
  };

  const handleUseStock = () => {
    if (!useForm.name?.value) {
      UseToast("Please select an item name", "error");
      return;
    }
    if (!useForm.qty || isNaN(Number(useForm.qty)) || Number(useForm.qty) <= 0) {
      UseToast("Please enter a valid quantity", "error");
      return;
    }
    if (!useForm.subbranch?.value) {
      UseToast("Please select a branch", "error");
      return;
    }

    useStock({
      name: useForm.name.value,
      desc: "",
      category: "packaging",
      qty: Number(useForm.qty),
      subbranch: useForm.subbranch.value,
      note: useForm.note || "used",
      inwardDate: useForm.inwardDate || undefined,
    });
  };

  const handleSort = (key: string) => {
    let data = [...tableData];
    let order = sortStat[key] === "asc" ? "desc" : "asc";

    const sortedData = data.sort((a, b) => {
      const valueA = key.split(".").reduce((obj, k) => obj?.[k], a) || "";
      const valueB = key.split(".").reduce((obj, k) => obj?.[k], b) || "";
      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0;
    });

    setSortStat({ [key]: order });
    setTableData(sortedData);
  };

  const GET_SORT_ICON = (key: string) => {
    const iconClass = "h-4 w-4 inline-block cursor-pointer ml-1";
    switch (sortStat[key]) {
      case "asc":
        return <ArrowUpIcon className={iconClass} onClick={() => handleSort(key)} />;
      case "desc":
        return <ArrowDownIcon className={iconClass} onClick={() => handleSort(key)} />;
      default:
        return <ArrowsUpDownIcon className={iconClass} onClick={() => handleSort(key)} />;
    }
  };

  const exportToExcel = () => {
    const headers = [
      "Sr. No.", "Inward date", "Item Name",
      "Description", "P.O No.", "PO Date", "Required delivery",
      "Received Qty", "Unit", "Inspection details",
      "Accepted Qty", "Rej.Qty", "Reason for Rej.Qty", "Branch"
    ];
    const colCount = headers.length;

    const fmtDt = (d: any) => d ? moment(d).format("DD-MM-YYYY") : "-";

    // Build data rows
    const dataRows = tableData.map((row, idx) => [
      idx + 1,
      fmtDt(row.inwardDate),
      row.name || "-",
      row.description || row.desc || "-",
      row._purchaseOrder?.poNo || "-",
      row._purchaseOrder?.poDate ? fmtDt(row._purchaseOrder.poDate) : "-",
      fmtDt(row.requiredDeliveryDate),
      row.qty || "-",
      row.measureUnit || "-",
      row.inspectionDetails || "-",
      Number(row.qty || 0) - Number(row.rejQty || 0),
      row.rejQty || "-",
      row.rejReason || "-",
      row._subbranch?.branch_name || "-",
    ]);

    // Date range label
    const dateLabel = filterStartDt || filterEndDt
      ? `Date Range: ${filterStartDt || "Start"} to ${filterEndDt || "End"}`
      : `Date Range: All`;

    // Build sheet data: 3 header rows + blank + column headers + data
    const sheetData: any[][] = [
      ["AIRFIELD EXPRESS PVT.LTD"],
      ["MATERIAL INWARD REPORT"],
      [dateLabel],
      [], // blank row
      headers,
      ...dataRows,
    ];

    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Merge cells for header rows (row 0, 1, 2)
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: colCount - 1 } },
    ];

    // Style title rows
    const titleStyle = { font: { bold: true, sz: 14 }, alignment: { horizontal: "center", vertical: "center" } };
    const subTitleStyle = { font: { bold: true, sz: 12 }, alignment: { horizontal: "center", vertical: "center" } };
    const dateStyle = { font: { sz: 10 }, alignment: { horizontal: "center", vertical: "center" } };
    if (ws["A1"]) ws["A1"].s = titleStyle;
    if (ws["A2"]) ws["A2"].s = subTitleStyle;
    if (ws["A3"]) ws["A3"].s = dateStyle;

    // Set column widths (narrow so header text wraps)
    ws["!cols"] = [
      { wch: 6 },   // Sr. No.
      { wch: 12 },  // Inward date
      { wch: 16 },  // Name of External provider
      { wch: 16 },  // Item Description
      { wch: 14 },  // P.O No. & Date
      { wch: 10 },  // Ordered Qty/Wt.
      { wch: 12 },  // Required delivery
      { wch: 12 },  // Received date
      { wch: 14 },  // Invoice no. & dt.
      { wch: 10 },  // Received Qty/Wt.
      { wch: 14 },  // Inspection details
      { wch: 10 },  // Accepted Qty
      { wch: 8 },   // Rej.Qty
      { wch: 14 },  // Reason for Rej.Qty
    ];

    // Set wrap text + bold + center on header row cells (row 4)
    const headerRowIdx = 4;
    const headerCellStyle = {
      font: { bold: true, sz: 10 },
      alignment: { wrapText: true, vertical: "center", horizontal: "center" },
      border: {
        top: { style: "thin" }, bottom: { style: "thin" },
        left: { style: "thin" }, right: { style: "thin" },
      },
      fill: { fgColor: { rgb: "D9E1F2" } },
    };
    for (let c = 0; c < colCount; c++) {
      const cellRef = XLSX.utils.encode_cell({ r: headerRowIdx, c });
      if (ws[cellRef]) {
        ws[cellRef].s = headerCellStyle;
      }
    }

    // Set thin borders on data cells
    const dataCellStyle = {
      alignment: { wrapText: true, vertical: "center" },
      border: {
        top: { style: "thin" }, bottom: { style: "thin" },
        left: { style: "thin" }, right: { style: "thin" },
      },
    };
    for (let r = headerRowIdx + 1; r < headerRowIdx + 1 + dataRows.length; r++) {
      for (let c = 0; c < colCount; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        if (ws[cellRef]) {
          ws[cellRef].s = dataCellStyle;
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Material Inward Report");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `Material_Inward_Report_${moment().format("YYYY-MM-DD")}.xlsx`);
  };

  // Calculate stock summary by item name
  const stockSummary = React.useMemo(() => {
    const summary: Record<string, number> = {};
    tableData.forEach((item) => {
      const name = item.name || "Unknown";
      summary[name] = (summary[name] || 0) + (Number(item.qty) || 0);
    });
    return summary;
  }, [tableData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 font-inter">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 px-6 py-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/10 p-2 rounded-lg">
              <CubeIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Stock Report</h1>
              <p className="text-slate-300 text-sm">Inventory Management</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              color="success"
              variant="solid"
              size="sm"
              startContent={<PlusIcon className="h-4 w-4" />}
              onClick={() => setAddModalOpen(true)}
            >
              Add Stock
            </Button>
            <Button
              color="primary"
              variant="solid"
              size="sm"
              startContent={<ArrowsRightLeftIcon className="h-4 w-4" />}
              onClick={() => setTransferModalOpen(true)}
            >
              Transfer
            </Button>
            <Button
              color="secondary"
              variant="flat"
              size="sm"
              startContent={<ArrowsRightLeftIcon className="h-4 w-4" />}
              onClick={() => navigate("/operation/box-tracker?tab=transfers")}
            >
              Transfers & In-Scan
            </Button>
            <Button
              color="warning"
              variant="solid"
              size="sm"
              startContent={<MinusCircleIcon className="h-4 w-4" />}
              onClick={() => setUseStockModalOpen(true)}
            >
              Stock Use
            </Button>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="px-4 -mt-4 relative z-20">
        <Card className="shadow-xl border-none overflow-visible">
          <CardBody className="p-4 overflow-visible">
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-52">
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                <Select
                  options={branchOptions}
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e)}
                  isClearable
                  placeholder="All Branches"
                  className="text-sm"
                />
              </div>
              <div className="flex items-center gap-2 self-end pb-1">
                <input
                  type="checkbox"
                  id="allTimeCheck"
                  checked={allTime}
                  onChange={(e) => setAllTime(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="allTimeCheck" className="text-sm font-medium text-gray-700 cursor-pointer">All Time</label>
              </div>
              {!allTime && (
                <AfDateRangePicker
                  startDate={filterStartDt}
                  endDate={filterEndDt}
                  setStartDate={(e: any) => setFilterStartDt(moment(e).format("YYYY-MM-DD"))}
                  setEndDate={(e: any) => setFilterEndDt(moment(e).format("YYYY-MM-DD"))}
                  label="Inward Date"
                  page="report"
                />
              )}
              <Button
                color="primary"
                variant="flat"
                size="md"
                startContent={<MagnifyingGlassIcon className="h-4 w-4" />}
                onClick={() => refetch()}
              >
                Search
              </Button>
              <Button
                color="default"
                variant="flat"
                size="md"
                startContent={<DocumentArrowDownIcon className="h-4 w-4" />}
                onClick={exportToExcel}
              >
                Export Excel
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Stock Summary Cards */}
      <div className="px-4 mt-4">
        <div className="grid grid-cols-6 md:grid-cols-4 sm:grid-cols-2 gap-3">
          {Object.entries(stockSummary).map(([name, qty]) => (
            <Card key={name} className="shadow-md">
              <CardBody className="p-3 text-center">
                <p className="text-xs text-gray-500 uppercase">{name}</p>
                <p className={`text-xl font-bold ${Number(qty) < 0 ? "text-red-600" : "text-green-600"}`}>
                  {qty}
                </p>
              </CardBody>
            </Card>
          ))}
        </div>
      </div>

      {/* Results Table */}
      <div className="px-4 mt-4 pb-8">
        <Card className="shadow-xl border-none overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 px-4 py-3">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                {tableData.length} Records
              </div>
            </div>
          </CardHeader>
          <CardBody className="p-0 overflow-x-auto">
            {isLoading || isFetching ? (
              <div className="flex justify-center items-center py-20">
                <Spinner size="lg" />
              </div>
            ) : (
              <table ref={tableRef} className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">Sr.</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">
                      Date {GET_SORT_ICON("createdAt")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">
                      Name {GET_SORT_ICON("name")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">
                      Category {GET_SORT_ICON("category")}
                    </th>
                    <th className="px-3 py-2 text-center font-semibold text-slate-700 border-b">
                      Qty {GET_SORT_ICON("qty")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">
                      Branch {GET_SORT_ICON("_subbranch.subbranch_name")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">PO No</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">Note</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700 border-b">Added By</th>
                    <th className="px-3 py-2 text-center font-semibold text-slate-700 border-b">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-10 text-gray-500">
                        No stock records found
                      </td>
                    </tr>
                  ) : (
                    tableData.map((row, idx) => (
                      <React.Fragment key={row.id || idx}>
                        <tr className="hover:bg-slate-50 border-b border-slate-100">
                          <td className="px-3 py-2 text-slate-600">{idx + 1}</td>
                          <td className="px-3 py-2 text-slate-600">{row.createdAt}</td>
                          <td className="px-3 py-2 font-medium text-slate-800">{row.name || "-"}</td>
                          <td className="px-3 py-2">
                            <Chip size="sm" variant="flat" color="default">
                              {row.category || "-"}
                            </Chip>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`font-semibold ${Number(row.qty) < 0 ? "text-red-600" : "text-green-600"}`}>
                              {Number(row.qty) > 0 ? "+" : ""}{row.qty}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-600">{row._subbranch?.branch_name || "-"}</td>
                          <td className="px-3 py-2 text-slate-600">{row._purchaseOrder?.poNo || "-"}</td>
                          <td className="px-3 py-2 text-slate-600">
                            {row.note === "transfer" ? (
                              <Chip size="sm" color="primary" variant="flat">Transfer</Chip>
                            ) : row.note === "used" ? (
                              <Chip size="sm" color="warning" variant="flat">Used</Chip>
                            ) : (row.note || "-")}
                          </td>
                          <td className="px-3 py-2 text-slate-600">{row._addedBy?.name || row._addedBy?.username || "-"}</td>
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                                detailViewRow === row.id
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              }`}
                              onClick={() => setDetailViewRow(detailViewRow === row.id ? null : row.id)}
                            >
                              <EyeIcon className="h-3.5 w-3.5" />
                              {detailViewRow === row.id ? "Hide" : "View"}
                            </button>
                          </td>
                        </tr>
                        {detailViewRow === row.id && (
                          <tr className="bg-blue-50 border-b border-blue-100">
                            <td colSpan={10} className="px-4 py-3">
                              <div className="grid grid-cols-4 gap-x-6 gap-y-2 text-sm">
                                <div><span className="text-gray-500">Description:</span> <span className="font-medium">{row.description || row.desc || "-"}</span></div>
                                <div><span className="text-gray-500">PO No:</span> <span className="font-medium">{row._purchaseOrder?.poNo || "-"}</span></div>
                                <div><span className="text-gray-500">PO Date:</span> <span className="font-medium">{row._purchaseOrder?.poDate ? moment(row._purchaseOrder.poDate).format("DD-MM-YYYY") : "-"}</span></div>
                                <div><span className="text-gray-500">Inward Date:</span> <span className="font-medium">{row.inwardDate ? moment(row.inwardDate).format("DD-MM-YYYY") : "-"}</span></div>
                                <div><span className="text-gray-500">Req. Delivery:</span> <span className="font-medium">{row.requiredDeliveryDate ? moment(row.requiredDeliveryDate).format("DD-MM-YYYY") : "-"}</span></div>
                                <div><span className="text-gray-500">Rej. Qty:</span> <span className="font-medium">{row.rejQty || "-"}</span></div>
                                <div><span className="text-gray-500">Rej. Reason:</span> <span className="font-medium">{row.rejReason || "-"}</span></div>
                                <div className="col-span-2"><span className="text-gray-500">Inspection:</span> <span className="font-medium">{row.inspectionDetails || "-"}</span></div>
                                <div><span className="text-gray-500">Measure Unit:</span> <span className="font-medium">{row.measureUnit || "-"}</span></div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Add Stock Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 z-[1003] overflow-y-auto">
          <div className="flex items-start justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 bg-black/50" onClick={() => setAddModalOpen(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl w-[90%] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Add Stock (Inward)</h3>
                <button onClick={() => setAddModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* PO Lookup Section */}
              <div className="border border-slate-200 rounded-lg p-4 mb-4 bg-slate-50">
                <div className="grid grid-cols-3 md:grid-cols-1 gap-4 items-end">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-600 mb-2">Link to Purchase Order (Optional)</h4>
                    <div className="flex items-end gap-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
                        <input
                          type="text"
                          className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={poLookupNo}
                          onChange={(e) => setPoLookupNo(e.target.value)}
                          placeholder="e.g. 000012"
                          maxLength={6}
                          onKeyDown={(e) => e.key === "Enter" && handlePoLookup()}
                        />
                      </div>
                      <Button color="primary" variant="flat" size="md" onClick={handlePoLookup} isLoading={poLookupLoading}>
                        Search
                      </Button>
                    </div>
                  </div>
                  {poLookupData && (
                    <>
                      <div className="text-sm space-y-1">
                        <div><span className="text-gray-500">Vendor:</span> <span className="font-medium">{poLookupData._vendor?.vendor_name || "-"}</span></div>
                        <div><span className="text-gray-500">PO Date:</span> <span className="font-medium">{poLookupData.poDate ? moment(poLookupData.poDate).format("DD MMM YYYY") : "-"}</span></div>
                      </div>
                      <div className="text-sm space-y-1">
                        <div><span className="text-gray-500">Total Qty:</span> <span className="font-medium">{poLookupData.items?.reduce((sum: number, it: any) => sum + (Number(it.qty) || 0), 0) || 0}</span></div>
                        <div className="text-xs text-gray-500">
                          {poLookupData.items?.map((it: any, i: number) => (
                            <span key={i}>{it.name || it.description}: {it.qty}{it.unit ? ` ${it.unit}` : ""}{i < poLookupData.items.length - 1 ? " | " : ""}</span>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Shared Fields: Branch, Dates */}
              <div className="grid grid-cols-3 md:grid-cols-1 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branch *</label>
                  <Select
                    options={branchOptions}
                    value={addShared.subbranch}
                    onChange={(e) => setAddShared({ ...addShared, subbranch: e })}
                    placeholder="Select branch..."
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Inward Date</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={addShared.inwardDate}
                    onChange={(e) => setAddShared({ ...addShared, inwardDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Required Delivery Date</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={addShared.requiredDeliveryDate}
                    onChange={(e) => setAddShared({ ...addShared, requiredDeliveryDate: e.target.value })}
                  />
                </div>
              </div>

              {/* Items Table */}
              <div className="border border-slate-200 rounded-lg overflow-hidden mb-4">
                <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b">
                  <h4 className="text-sm font-semibold text-slate-600">Items</h4>
                  <Button size="sm" color="primary" variant="flat" onClick={addNewItemRow} startContent={<PlusIcon className="h-4 w-4" />}>
                    Add Row
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium w-[35%]">Item Name *</th>
                        <th className="px-3 py-2 text-left font-medium w-[22%]">Category</th>
                        <th className="px-3 py-2 text-left font-medium w-[15%]">Recv Qty *</th>
                        <th className="px-3 py-2 text-left font-medium w-[13%]">Unit</th>
                        <th className="px-3 py-2 text-left font-medium w-[13%]">Rej Qty</th>
                        <th className="px-3 py-2 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {addItems.map((item, idx) => (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="px-2 py-2">
                            <Select
                              options={STOCK_ITEM_OPTIONS}
                              value={item.name}
                              onChange={(e) => updateAddItem(idx, "name", e)}
                              placeholder="Select..."
                              menuPortalTarget={document.body}
                              styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }), control: (base) => ({ ...base, minHeight: 34 }) }}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <Select
                              options={CATEGORY_OPTIONS}
                              value={item.category}
                              onChange={(e) => updateAddItem(idx, "category", e)}
                              placeholder="Category"
                              menuPortalTarget={document.body}
                              styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }), control: (base) => ({ ...base, minHeight: 34 }) }}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              value={item.qty}
                              onChange={(e) => updateAddItem(idx, "qty", e.target.value)}
                              placeholder="0"
                              min="0"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <select
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              value={item.measureUnit}
                              onChange={(e) => updateAddItem(idx, "measureUnit", e.target.value)}
                            >
                              {["Pcs", "Kg", "Ltr", "Mtr", "Box", "Nos", "Set", "Roll"].map((u) => (
                                <option key={u} value={u}>{u}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              value={item.rejQty}
                              onChange={(e) => updateAddItem(idx, "rejQty", e.target.value)}
                              placeholder="0"
                              min="0"
                            />
                          </td>
                          <td className="px-2 py-2 text-center">
                            {addItems.length > 1 && (
                              <button onClick={() => removeItemRow(idx)} className="text-red-400 hover:text-red-600">
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Inspection & Note (shared) */}
              <div className="grid grid-cols-3 md:grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Reason</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={addShared.rejReason}
                    onChange={(e) => setAddShared({ ...addShared, rejReason: e.target.value })}
                    placeholder="Reason (if any)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Inspection Details</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={addShared.inspectionDetails}
                    onChange={(e) => setAddShared({ ...addShared, inspectionDetails: e.target.value })}
                    placeholder="Inspection details"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={addShared.note}
                    onChange={(e) => setAddShared({ ...addShared, note: e.target.value })}
                    placeholder="Optional note"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6 border-t pt-4">
                <Button color="default" variant="flat" onClick={() => setAddModalOpen(false)}>
                  Cancel
                </Button>
                <Button color="success" onClick={handleAddStock} isLoading={isCreating}>
                  Save All Items
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Stock Modal */}
      {transferModalOpen && (
        <div className="fixed inset-0 z-[1003] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-6">
            <div className="fixed inset-0 bg-black/50" onClick={() => setTransferModalOpen(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4 pb-2 border-b">
                <div className="flex items-center gap-2">
                  <ArrowsRightLeftIcon className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-slate-800">Dispatch Stock Transfer</h3>
                </div>
                <button onClick={() => setTransferModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Transfer Mode Switch */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Transfer Mode</label>
                  <div className="flex p-1 bg-slate-100 rounded-lg text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => setTransferMode("bulk")}
                      className={`flex-1 py-1.5 rounded-md transition-all ${
                        transferMode === "bulk"
                          ? "bg-white text-slate-800 shadow-xs font-bold"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      Bulk Transfer (No QR)
                    </button>
                    <button
                      type="button"
                      onClick={() => setTransferMode("qr")}
                      className={`flex-1 py-1.5 rounded-md transition-all ${
                        transferMode === "qr"
                          ? "bg-white text-blue-600 shadow-xs font-bold"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      Scan Box QRs ({transferQrCodes.length})
                    </button>
                  </div>
                </div>

                {/* Branches */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">From Branch *</label>
                    <Select
                      options={branchOptions}
                      value={transferForm.fromBranch}
                      onChange={(e) => setTransferForm({ ...transferForm, fromBranch: e })}
                      placeholder="Source branch..."
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">To Branch *</label>
                    <Select
                      options={branchOptions}
                      value={transferForm.toBranch}
                      onChange={(e) => setTransferForm({ ...transferForm, toBranch: e })}
                      placeholder="Destination branch..."
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                    />
                  </div>
                </div>

                {/* Item Name & Category */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Item Name *</label>
                    <Select
                      options={STOCK_ITEM_OPTIONS.filter((o) => o.value !== "other")}
                      value={transferForm.name}
                      onChange={(e) => setTransferForm({ ...transferForm, name: e })}
                      placeholder="Select item..."
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                    <Select
                      options={CATEGORY_OPTIONS}
                      value={transferForm.category}
                      onChange={(e) => setTransferForm({ ...transferForm, category: e })}
                      placeholder="Category..."
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                    />
                  </div>
                </div>

                {/* Mode specific fields */}
                {transferMode === "bulk" ? (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Quantity *</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono font-bold"
                      value={transferForm.qty}
                      onChange={(e) => setTransferForm({ ...transferForm, qty: e.target.value })}
                      placeholder="Enter quantity"
                      min="1"
                    />
                  </div>
                ) : (
                  <div className="space-y-2 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                        <QrCodeIcon className="w-4 h-4 text-blue-600" />
                        <span>Scan Outgoing Box QRs ({transferQrCodes.length})</span>
                      </label>
                      {transferQrCodes.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setTransferQrCodes([])}
                          className="text-[11px] text-red-600 hover:underline"
                        >
                          Clear All
                        </button>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <input
                        ref={transferQrInputRef}
                        type="text"
                        value={transferQrInput}
                        onChange={(e) => setTransferQrInput(e.target.value.toUpperCase())}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddTransferQr(e);
                          }
                        }}
                        placeholder="Scan Box QR (e.g. 123123, BX-123123, AHD123123)..."
                        className="flex-1 px-3 py-1.5 text-xs font-mono font-bold border border-slate-300 rounded-lg"
                      />
                      <Button size="sm" color="primary" onClick={handleAddTransferQr}>
                        Add
                      </Button>
                    </div>

                    {transferQrCodes.length > 0 && (
                      <div className="max-h-28 overflow-y-auto flex flex-wrap gap-1.5 pt-1">
                        {transferQrCodes.map((code) => (
                          <span
                            key={code}
                            className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 font-mono text-[11px] font-bold px-2 py-0.5 rounded border border-blue-200"
                          >
                            <span>{code}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveTransferQr(code)}
                              className="text-blue-400 hover:text-red-600 ml-0.5"
                            >
                              &times;
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Dispatch Note / Vehicle (optional)
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={transferForm.note}
                    onChange={(e) => setTransferForm({ ...transferForm, note: e.target.value })}
                    placeholder="e.g. Dispatched via truck #GJ01-1234"
                  />
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-3 mt-4 text-xs text-blue-900 space-y-1">
                <p className="font-bold flex items-center gap-1.5">
                  <ArrowsRightLeftIcon className="h-4 w-4 text-blue-600" />
                  <span>In-Transit Dispatch Flow</span>
                </p>
                <p className="text-blue-800 leading-relaxed">
                  Deducts {transferMode === "qr" ? transferQrCodes.length : transferForm.qty || "0"} units from{" "}
                  <strong>{transferForm.fromBranch?.label || "Origin"}</strong> and initiates an in-transit transfer (`TR-XXXX`).
                  <strong> {transferForm.toBranch?.label || "Destination"}</strong> will receive stock once they in-scan and receive the shipment.
                </p>
              </div>

              <div className="flex justify-end gap-2 mt-6 border-t pt-4">
                <Button color="default" variant="flat" onClick={() => setTransferModalOpen(false)}>
                  Cancel
                </Button>
                <Button color="primary" onClick={handleTransferStock} isLoading={isTransferring}>
                  Dispatch Transfer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Use Stock Modal */}
      {useStockModalOpen && (
        <div className="fixed inset-0 z-[1003] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setUseStockModalOpen(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Stock Use</h3>
                <button onClick={() => setUseStockModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Name *</label>
                  <Select
                    options={STOCK_ITEM_OPTIONS.filter((o) => o.value !== "other")}
                    value={useForm.name}
                    onChange={(e) => setUseForm({ ...useForm, name: e })}
                    placeholder="Select item..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity to Use *</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={useForm.qty}
                    onChange={(e) => setUseForm({ ...useForm, qty: e.target.value })}
                    placeholder="Enter quantity"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branch *</label>
                  <Select
                    options={branchOptions}
                    value={useForm.subbranch}
                    onChange={(e) => setUseForm({ ...useForm, subbranch: e })}
                    placeholder="Select branch..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={useForm.inwardDate}
                    onChange={(e) => setUseForm({ ...useForm, inwardDate: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={useForm.note}
                    onChange={(e) => setUseForm({ ...useForm, note: e.target.value })}
                    placeholder="Purpose of usage (optional)"
                    rows={2}
                  />
                </div>
              </div>

              <div className="bg-yellow-50 rounded-lg p-3 mt-4 text-sm text-yellow-800">
                <p>
                  This will <strong>subtract {useForm.qty || "X"}</strong> from{" "}
                  <strong>{useForm.subbranch?.label || "selected branch"}</strong>
                </p>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button color="default" variant="flat" onClick={() => setUseStockModalOpen(false)}>
                  Cancel
                </Button>
                <Button color="warning" onClick={handleUseStock} isLoading={isUsingStock}>
                  Record Usage
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockReport;
